import { ReactNode, useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select, Tag, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import SearchModal from "../../SearchModal";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import axios from "axios";
import Swal from "sweetalert2";
import { Crypter } from "../../../Functions/Security/Crypter";
import { OpeApi } from "../../../Services/ApiOpeLiees";

const searchOperat: ParamsPicklist = { CodeAgence: '', Table: "OPERAT", Colonne1: "CODOPE", Colonne2: "LIBELL", Colagence: '101', Choix: 22 };

interface GroupeOption {
    IDGRP: number;
    CODGRP: string;
    LIBGRP: string;
    LOGIQUE: string;
    ACTIVE?: boolean;
    DATCRE?: string;
    EXPRCANON?: string;
}

interface ConditionOption {
    CODCOND: string;
    LIBCOND: string;
    ACTIVE?: boolean;
    DATCRE?: string;
    EXPRCANON?: string;
}

// Au survol d'une option du select : title HTML natif portant l'expression
// (meme position que l'ancien tooltip "CODE — Libelle" du navigateur).
const OptionAvecExpr = (props: { children: ReactNode }) => (
    <span style={{ display: 'block' }}>{props.children}</span>
);

const GroupeBadge = (props: { form: any; index: number }) => {
    const regle = Form.useWatch(["OPERELIES", props.index, "REGLE_LIB"], props.form);
    const active = Form.useWatch(["OPERELIES", props.index, "ACTIVE"], props.form);
    return (
        <>
            <Tag color={regle ? "blue" : "default"} style={{ marginRight: 4 }}>{regle ?? "—"}</Tag>
            {active === false && <Tag color="default" style={{ marginRight: 4 }}>Inactive</Tag>}
        </>
    );
};

const OperationsLiees = (props: { form: any; disabled?: boolean; authHeader: () => string }) => {

    const [formLiees] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);
    const [rowIndex, setRowIndex] = useState<number>(0);
    const [selRegle, setSelRegle] = useState<string | null>(null);   // "C:<codcond>" | "G:<idgrp>"
    const [groupes, setGroupes] = useState<GroupeOption[]>([]);
    const [conditions, setConditions] = useState<ConditionOption[]>([]);

    const liees: any[] = Form.useWatch("OPERELIES", props.form) ?? [];

    // Une meme regle (condition ou groupe) ne peut pas etre affectee a
    // deux operations reliees de la meme chaine : les cles deja prises
    // par les AUTRES lignes sont desactivees dans le selecteur. La ligne
    // courante garde sa propre regle selectionnable.
    const reglesPrises = new Set(
        liees
            .map((l, i) => i !== rowIndex
                ? (l?.GROUPE_ID ? `G:${l.GROUPE_ID}` : l?.CODCOND ? `C:${l.CODCOND}` : null)
                : null)
            .filter((k): k is string => !!k)
    );

    // Chargement des groupes et conditions (selecteur condition/groupe).
    // Les regles inactives sont conservees : une etape INACTIVE peut
    // encore pointer une regle desactivee (le backend l'autorise) — elle
    // doit rester lisible dans le selecteur, mais non selectionnable.
    useEffect(() => {
        const appel = (url: string) => axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url,
            headers: { 'Authorization': props.authHeader(), 'Content-Type': 'application/json' }
        }).then((response) => response.data ? JSON.parse(Crypter.decrypt(response.data)) : null);

        appel(OpeApi.ReadGroupes).then((res) => {
            if (res?.StatutCode === 200 && res.Result?.Table)
                setGroupes(res.Result.Table.map((g: any) => ({ ...g, ACTIVE: g.ACTIVE === true || g.ACTIVE === 1 })));
        }).catch((err) => console.log("ReadGroupes:", err));

        appel(OpeApi.ReadConditions).then((res) => {
            if (res?.StatutCode === 200 && res.Result?.Table)
                setConditions(res.Result.Table.map((c: any) => ({ ...c, ACTIVE: c.ACTIVE === true || c.ACTIVE === 1 })));
        }).catch((err) => console.log("ReadConditions:", err));
    }, []);

    useEffect(() => {
        liees.forEach((r, i) => {
            formLiees.setFieldsValue({
                ["OPLIE_" + i]: r?.CODELIE ?? "",
                ["lib_OPLIE_" + i]: r?.LIBELIE ?? ""
            });
        });
    }, [liees]);

    const syncHidden = (index: number, code: string, libelle?: string) => {
        props.form.setFieldValue(["OPERELIES", index, "CODELIE"], code ?? "");
        props.form.setFieldValue(["OPERELIES", index, "LIBELIE"], libelle ?? formLiees.getFieldValue("lib_OPLIE_" + index) ?? "");
    };

    const openModalGroupe = (index: number) => {
        setRowIndex(index);
        const gid = props.form.getFieldValue(["OPERELIES", index, "GROUPE_ID"]);
        const cnd = props.form.getFieldValue(["OPERELIES", index, "CODCOND"]);
        setSelRegle(gid ? `G:${gid}` : cnd ? `C:${cnd}` : null);
        setModalOpen(true);
    };

    const saveModalGroupe = () => {
        // Garde cote validation : meme si l'option est desactivee, on
        // refuse explicitement une regle deja affectee a une autre ligne.
        if (selRegle && reglesPrises.has(selRegle)) {
            Swal.fire({ title: "Règle déjà utilisée", text: "Cette règle est déjà affectée à une autre opération reliée de cette chaîne.", icon: "warning" });
            return;
        }
        let gid: number | null = null, cnd: string | null = null, lib: string | null = null;
        if (selRegle?.startsWith("G:")) {
            gid = Number(selRegle.slice(2));
            const g = groupes.find(x => x.IDGRP === gid);
            lib = g ? `${g.CODGRP} — ${g.LIBGRP}` : `G${gid}`;
        } else if (selRegle?.startsWith("C:")) {
            cnd = selRegle.slice(2);
            const c = conditions.find(x => x.CODCOND === cnd);
            lib = c ? `${c.CODCOND} — ${c.LIBCOND}` : cnd;
        }
        props.form.setFieldValue(["OPERELIES", rowIndex, "GROUPE_ID"], gid);
        props.form.setFieldValue(["OPERELIES", rowIndex, "CODCOND"], cnd);
        props.form.setFieldValue(["OPERELIES", rowIndex, "REGLE_LIB"], lib);
        // BASE_CALCUL imposee a 'INITIAL' (valeur metier unique)
        props.form.setFieldValue(["OPERELIES", rowIndex, "BASE_CALCUL"], "INITIAL");
        setModalOpen(false);
    };

    return (
        <>
            <Form.List name="OPERELIES">
                {(fields, { add, remove, move }) => (
                    <>
                        {fields.length === 0 && (
                            <Box style={{ textAlign: "center", color: "#999", padding: "8px 0", fontSize: 12 }}>
                                Aucune opération reliée
                            </Box>
                        )}
                        {fields.map((field, idx) => (
                            <Box key={field.key} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                                <Tag color="#116599" style={{ minWidth: 26, textAlign: "center", marginRight: 4 }}>{idx + 1}</Tag>
                                <Box style={{ flexGrow: 1, minWidth: 0 }}>
                                    <Form.Item name={[field.name, "CODELIE"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "LIBELIE"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "GROUPE_ID"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "CODCOND"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "REGLE_LIB"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "BASE_CALCUL"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "ACTIVE"]} hidden><Input /></Form.Item>
                                    <Form form={formLiees} component={false} disabled={props.disabled}>
                                        <SearchModal
                                            width={0}
                                            Titre="Recherche opération"
                                            notRequired
                                            label={`Opération reliée ${idx + 1}`}
                                            name={"OPLIE_" + idx}
                                            Parametre={searchOperat}
                                            Taille1={"100%"}
                                            Taille2={"100%"}
                                            form={formLiees}
                                            handleFunction={(code: string, lib?: string) => syncHidden(idx, code, lib)}
                                        />
                                    </Form>
                                </Box>
                                <GroupeBadge form={props.form} index={idx} />
                                <Button size="small" icon={<SettingOutlined />} title="Règle d'exécution / mode" onClick={() => openModalGroupe(idx)} />
                                <Button size="small" icon={<ArrowUpOutlined />} title="Monter" disabled={idx === 0} onClick={() => move(idx, idx - 1)} />
                                <Button size="small" icon={<ArrowDownOutlined />} title="Descendre" disabled={idx === fields.length - 1} onClick={() => move(idx, idx + 1)} />
                                <Button size="small" danger icon={<DeleteOutlined />} title="Retirer" onClick={() => remove(idx)} />
                            </Box>
                        ))}
                        <Button size="small" icon={<PlusOutlined />} onClick={() => add({ CODELIE: "", LIBELIE: "", GROUPE_ID: null, CODCOND: null, BASE_CALCUL: "INITIAL", ACTIVE: true })} block>
                            Ajouter une opération reliée
                        </Button>
                    </>
                )}
            </Form.List>

            <Modal
                title="Conditionnement de l'opération reliée"
                open={modalOpen}
                onOk={saveModalGroupe}
                onCancel={() => setModalOpen(false)}
                okText="Valider"
                okButtonProps={{ style: { color: "#fff" } }}
                cancelText="Annuler"
                zIndex={2300}
            >
                <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                    Règle d'exécution (vide = exécution inconditionnelle) :
                </Typography.Text>
                <Select
                    style={{ width: "100%", marginBottom: 16 }}
                    allowClear
                    showSearch
                    // Recherche sur code+libelle via champ custom 'search'
                    // (un prop 'title' deviendrait un title HTML natif =
                    // second tooltip en doublon de l'expression).
                    filterOption={(input, option) =>
                        ((option as any)?.search ?? '').toLowerCase().includes(input.toLowerCase())}
                    placeholder="Aucune règle — inconditionnel"
                    value={selRegle}
                    onChange={(v) => setSelRegle(v ?? null)}
                    options={
                        // Tri par ordre de création (plus recentes en premier),
                        // chaque option porte son badge Unique/Groupe.
                        // Regle inactive : grisee et non selectionnable,
                        // sauf si c'est deja la regle de l'etape en cours.
                        // Regle deja prise par une autre ligne : grisee et
                        // marquee "Deja utilisee".
                        [...conditions.map(c => ({
                            value: `C:${c.CODCOND}`,
                            search: `${c.CODCOND} — ${c.LIBCOND}`,
                            title: c.EXPRCANON || 'Expression non disponible',
                            datcre: c.DATCRE ?? '',
                            disabled: reglesPrises.has(`C:${c.CODCOND}`)
                                || (!c.ACTIVE && selRegle !== `C:${c.CODCOND}`),
                            label: (
                                <OptionAvecExpr>
                                    <Tag color="blue" style={{ marginRight: 6 }}>Unique</Tag>
                                    {c.CODCOND} — {c.LIBCOND}
                                    {!c.ACTIVE && <Tag style={{ marginLeft: 6 }}>Désactivée</Tag>}
                                    {reglesPrises.has(`C:${c.CODCOND}`) && <Tag color="orange" style={{ marginLeft: 6 }}>Déjà utilisée</Tag>}
                                </OptionAvecExpr>
                            )
                        })),
                        ...groupes.map(g => ({
                            value: `G:${g.IDGRP}`,
                            search: `${g.CODGRP} — ${g.LIBGRP}`,
                            title: g.EXPRCANON || 'Expression non disponible',
                            datcre: g.DATCRE ?? '',
                            disabled: reglesPrises.has(`G:${g.IDGRP}`)
                                || (!g.ACTIVE && selRegle !== `G:${g.IDGRP}`),
                            label: (
                                <OptionAvecExpr>
                                    <Tag color="green" style={{ marginRight: 6 }}>Groupe</Tag>
                                    {g.CODGRP} — {g.LIBGRP} ({g.LOGIQUE})
                                    {!g.ACTIVE && <Tag style={{ marginLeft: 6 }}>Désactivé</Tag>}
                                    {reglesPrises.has(`G:${g.IDGRP}`) && <Tag color="orange" style={{ marginLeft: 6 }}>Déjà utilisée</Tag>}
                                </OptionAvecExpr>
                            )
                        }))]
                        .sort((a, b) => b.datcre.localeCompare(a.datcre))
                    }
                />
                <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                    Mode d'exécution : chaque opération reliée est calculée
                    sur les <b>valeurs initiales</b> de l'opération principale.
                </Typography.Text>
            </Modal>
        </>
    );
};

export default OperationsLiees;
